<template>
    <div>
        <x-dialog
            v-model="showRepaymentDialog"
            width="900"
            max-width="900"
            content-class="overflow-scroll"
        >
            <x-form ref="repaymentForm" v-model="repaymentValid">
                <x-container fluid>
                    <x-layout>
                        <x-flex xs12>
                            <h2>Make repayment</h2>
                        </x-flex>
                    </x-layout>

                    <x-layout>
                        <x-flex xs6>
                            <x-menu
                                v-model="repaymentDateInput"
                                :close-on-content-click="false"
                                lazy
                                transition="scale-transition"
                                offset-y
                                max-width="300"
                                full-width
                            >
                                <template v-slot:activator="{ on }">
                                    <x-text-field
                                        v-model="repaymentDate"
                                        label="Repayment date"
                                        placeholder=" "
                                        append-icon="event"
                                        outline
                                        readonly
                                        v-on="on"
                                    />
                                </template>

                                <x-date-picker
                                    v-model="repaymentDate"
                                    @input="repaymentDateInput = false"
                                    :min="declaration.inception_date"
                                    :max="declaration.expiry_date"
                                />
                            </x-menu>

                            <x-currency-text-field
                                v-model="repaymentAmount"
                                label="Repayment amount"
                                placeholder=" "
                                :prefix="declaration.currency.code"
                                :rules="[
                                    () => ruleRequired(repaymentAmount, 'Repayment amount'),
                                    () => ruleGreaterThan(repaymentAmount, 0, 'Repayment amount'),
                                    () => ruleLessThanOrEqualTo(repaymentAmount, declaration.remaining_exposure, 'Repayment amount')
                                ]"
                                outline
                            />
                            <x-text-field
                                v-model="endorsementReference"
                                class="required mt-2"
                                label="Endorsement reference"
                                placeholder=" "
                                required
                                maxlength="10"
                                :rules="[
                                    (v) => ruleAlphanumeric(v, 'Endorsement reference'),
                                    (v) => ruleRequired(v, 'Endorsement reference')]"
                                outline
                            />
                        </x-flex>

                        <x-flex xs6>
                            <data-list v-model="repaymentDataListItems" />
                        </x-flex>
                    </x-layout>

                    <x-layout>
                        <x-flex xs12>
                            <document-list
                                :insurer-list="insurerList"
                                v-if="repayment.id"
                                class="mt-3"
                                upload
                                insurer-specific-upload
                                :facility-id="declaration.facility_id"
                                :documentable-id="repayment.id"
                                documentable-type="endorsement"
                                column
                                @update-document-categories="handleDocumentCategories"
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout class="response-actions" justify-space-between>
                        <x-flex class="text-xs-left">
                            <x-btn
                                @click="saveRepaymentChanges"
                                class="ma-0"
                                color="primary"
                                small
                                :loading="repaymentLoading"
                                :disabled="repaymentLoading"
                            >
                                Save Changes

                                <x-icon class="ml-2">trending_flat</x-icon>
                            </x-btn>
                        </x-flex>
                        <x-flex class="text-xs-right">
                            <x-btn
                                @click="submitRepayment"
                                class="ma-0"
                                color="primary"
                                small
                                :loading="repaymentLoading"
                                :disabled="!repaymentValid"
                            >
                                Submit

                                <x-icon class="ml-2">trending_flat</x-icon>
                            </x-btn>
                        </x-flex>
                    </x-layout>
                </x-container>
            </x-form>
        </x-dialog>

          <modal
            ref="repaymentConfirmation"
            title="Submit repayment"
            confirmation
            action-text="Confirm"
        >
            <p>
                You are about to submit a repayment of {{ repaymentAmount | currency(declaration.currency.code) }}
                <br>
                This will take effect on {{ repaymentDate | formatDate }}
            </p>
        </modal>
        <modal ref="notUniqueEndorsementReference" title="Error">
            <p>Endorsement reference not unique.</p>
            <p>Please change it.</p>
        </modal>
        <modal
            ref="noEndorsementDocument"
            title="Endorsement document"
            confirmation
        >
            <div class="text-xs-left">
                <p>You have not uploaded an Endorsement Document.</p>

                <p>Are you sure that you wish to proceed?</p>
            </div>
        </modal>
    </div>
</template>

<script>
import DataList from '~/components/Shared/UIComponents/DataList';
import DocumentList from '~/components/Shared/UIComponents/DocumentList';
import rules from '~/mixins/rules';
import Modal from '~/components/Shared/UIComponents/Modal';

export default {
    name: 'RepaymentsDialog',
    components: {
        DataList,
        DocumentList,
        Modal
    },
    mixins: [rules],
    props: {
        declaration: {
            type: Object,
            default: {}
        },
    },
    data() {
        return {
            repaymentValid: true,
            repaymentDateInput: false,
            repaymentDate: this.$moment(this.declaration.inception_date).isAfter(this.$moment()) ? this.declaration.inception_date : this.$moment(),
            repaymentAmount: '',
            repaymentLoading: true,
            showRepaymentDialog: false,
            repayment: {},
            endorsementReference: null,
            endorsementReferenceExists: false,
            documentCategoriesFromChild: [],
        }
    },
    created() {
        this.$eventBus.$on('open-dialog', async (repayment) => {
           await this.setUpData(repayment);
        });
    },
    computed: {
        repaymentDataListItems() {
            const repaymentAmount = this.repaymentAmount || 0;
            const originalExposure = this.isCommitment || this.isInvoice ? this.declaration.limit_of_liability : this.declaration.current_contract_value;
            const repaidAmount = originalExposure - this.declaration.remaining_exposure + repaymentAmount;
            const totalCurrentExposure = this.declaration.remaining_exposure - repaymentAmount;
            const insuredExposure = (this.declaration.indemnity / 100) * totalCurrentExposure;
            const repaymentDataListAry = [
                {
                    text: this.currentContractValueLabel,
                    value: this.$options.filters.currency(this.declaration.current_contract_value, this.declaration.currency.code)
                },
            ];

            if (this.isCommitment || this.isInvoice) {
                repaymentDataListAry.push(
                    {
                        text: 'Limit of liability',
                        value: this.$options.filters.currency(originalExposure, this.declaration.currency.code)
                    }
                );
            }

            repaymentDataListAry.push(
                {
                    text: 'Repaid amount',
                    value: this.$options.filters.currency(repaidAmount, this.declaration.currency.code)
                },
                {
                    text: this.isDirectOCPAProduct ? 'Total insured\'s participation' : 'Total current exposure',
                        value: this.$options.filters.currency(totalCurrentExposure, this.declaration.currency.code)
                }
            );

            repaymentDataListAry.push(
                {
                    text: this.getIndemnityLabel,
                    value: this.$options.filters.percent(this.declaration.indemnity)
                }
            );

            if (!this.isCommitment && !this.isInvoice) {
                repaymentDataListAry.push(
                    {
                        text: 'Insured exposure',
                        value: this.$options.filters.currency(insuredExposure, this.declaration.currency.code)
                    }
                );
            }

            return repaymentDataListAry;
        },
        isCommitment() {
            return this.declaration.quote_type?.key === 'commitment';
        },
        isInvoice() {
            return this.declaration.quote_type?.key === 'invoice';
        },
        currentContractValueLabel() {
            const productConfigKey = this.declaration.product_config_key;
            switch (productConfigKey) {
                case 'equity':
                    return 'Equity amount';
                case 'direct_ocpa':
                    return 'Insured\'s participation';
                case 'invoice':
                    return 'Guaranteed value';
                default:
                    return 'Contract amount';
            }
        },
        isDirectOCPAProduct() {
            return this.declaration.product && this.declaration.product.key === 'direct_ocpa';
        },
        getIndemnityLabel() {
            return this.isCommitment || this.isInvoice ? 'Maximum indemnity' : 'Indemnity';
        },
        insurerList: function () {
            return this.declaration.lines.map(line => {
                if (line.captive_insurer_company_id) {
                    return line.firm_order_offer.captive_insurer_company;
                }
                return line.firm_order_offer.insurer_company;
            })
        },
        repaymentDateDefaultValue() {
            const inceptionDate = this.$moment(this.declaration.inception_date);
            const today = this.$moment();

            const date = inceptionDate.isAfter(today) ? inceptionDate : today;
            return date.format('YYYY-MM-DD');
        }
    },
    methods: {
        async saveRepaymentChanges() {
            if (this.endorsementReference) {
                await this.checkIsReferenceUnique();
            }

            if (this.endorsementReferenceExists) {
                return;
            }

            this.loading = true;
            this.repaymentLoading = true;

            await axios.post(this.$root.api(`broker/declaration/${this.declaration.id}/repayment/${this.repayment.id}/save-changes`), {
                effective_date: this.repaymentDate,
                repayment_amount: this.repaymentAmount,
                reference: this.endorsementReference
            });

            this.loading = false;
            this.repaymentLoading = false;
        },
        async submitRepayment() {
            await this.checkIsReferenceUnique();

            if (this.endorsementReferenceExists) {
                return;
            }

            if (this.documentCategoriesFromChild.some(doc => doc.text === 'Endorsement')) {
                if (await this.$refs.noEndorsementDocument.open() === false) {
                    return;
                }
            }

            if (await this.$refs.repaymentConfirmation.open() === false) {
                return;
            }

            this.loading = true;
            this.repaymentLoading = true;

            await axios.post(this.$root.api(`broker/declaration/${this.declaration.id}/repayment/${this.repayment.id}/apply`), {
                effective_date: this.repaymentDate,
                repayment_amount: this.repaymentAmount,
                reference: this.endorsementReference
            }).then(() => {

                this.$eventBus.$emit('update-data');
                this.showRepaymentDialog = false;
            });
        },
        async setUpData(repayment) {
            this.repayment = repayment;

            this.repaymentDate = this.repayment.effective_date ? this.repayment.effective_date : this.repaymentDateDefaultValue;
            this.repaymentAmount = this.repayment.repayment_amount;
            this.endorsementReference = this.repayment.reference;

            this.repaymentLoading = false;
            this.showRepaymentDialog = true;
        },
        goToDeclaration() {
            this.$router.push({path: `/agent/risk/${this.declaration.submission_id}`});
        },
        async checkIsReferenceUnique() {
            await axios.get(this.$root.api(`broker/amendment/check-reference`), {
                params: {
                    declaration_id: this.declaration.id,
                    entity_reference: this.endorsementReference,
                    current_endorsement_id: this.repayment.id
                }
            }).then(response => {
                this.endorsementReferenceExists = response.data.exists;
            });
            if (this.endorsementReferenceExists) {
                if (await this.$refs.notUniqueEndorsementReference.open() === false) {
                    return;
                }
                window.scroll({
                    top: 0,
                    left: 0,
                    behavior: 'smooth'
                });
            }
        },
        handleDocumentCategories(categories) {
            this.documentCategoriesFromChild = categories;
        },
    },
    watch: {
        showRepaymentDialog(newValue) {
            if (!newValue) {
                this.$eventBus.$emit('update-data');
            }
        }
    }
}
</script>

<style>

</style>
